// Materialization approval — the Founder's UI control for the HELD diff
// (Founder directive, 2026-08-25: the panel displayed the held diff but
// pointed at "the existing founder materialize route"; no governed
// materialization-approval UI existed anywhere in Cipher, so this adds one
// using EXACTLY that existing route, POST /api/organization/materialize).
//
// SAME-COMPUTATION GUARANTEE, stated where the Founder reads it: preview
// and apply call the identical backend function
// (`materialize.materialize(db, dry_run=...)`) — one implementation, one
// projection, one fingerprint comparison; `dry_run` is the only
// difference. The diff shown IS what apply will do, by construction.
//
// Self-contained on the panel's own patterns (axios+API, verbatim error
// rendering, stage-a classes, confirm-before-write) so the surrounding
// panel file needs only an import and a render line.
import axios from "axios";
import React, { useState } from "react";

import { SectionTitle } from "../../components/PageHead";
import { API } from "../../data/client";

const MATERIALIZE_PATH = "/organization/materialize";

async function postMaterialize(dryRun) {
  try {
    const res = await axios.post(
      `${API}${MATERIALIZE_PATH}`,
      { dry_run: dryRun },
      { withCredentials: true },
    );
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    const httpStatus = err?.response?.status ?? null;
    const detail =
      err?.response?.data?.detail || err?.message || "Request failed";
    return { status: "error", detail: String(detail), httpStatus };
  }
}

function DiffRow({ change }) {
  return (
    <li className="stage-a-steps">
      <strong>{change.title || change.agent_id}</strong>
      {" — "}
      {change.status}
      {change.changed_dimensions?.length > 0 && (
        <> · dimensions: {change.changed_dimensions.join(", ")}</>
      )}
      {change.quiet_backfill_dimensions?.length > 0 && (
        <> · quiet backfill: {change.quiet_backfill_dimensions.join(", ")}</>
      )}
    </li>
  );
}

export default function MaterializeApproval() {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadPreview = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    const out = await postMaterialize(true);
    setBusy(false);
    if (out.status === "ok") setPreview(out.data);
    else setError(out.detail);
  };

  const apply = async () => {
    const changed = (preview?.changes || []).filter(
      (c) => c.status !== "unchanged",
    );
    const ok = window.confirm(
      `Apply materialization now?\n\nThis writes the organization records ` +
        `for ${changed.length} changed agent(s) shown in the diff below ` +
        `(counts: ${JSON.stringify(preview?.counts || {})}).\n\n` +
        `Preview and apply run the identical computation; dry_run is the ` +
        `only difference.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    const out = await postMaterialize(false);
    setBusy(false);
    if (out.status === "ok") setResult(out.data);
    else setError(out.detail);
  };

  const changes = (preview?.changes || []).filter(
    (c) => c.status !== "unchanged",
  );

  return (
    <section className="stage-a-card">
      <SectionTitle>Materialization approval</SectionTitle>
      <p className="stage-a-note">
        Preview the complete held diff, then apply it through the existing
        Founder materialize route. Preview and apply run the identical
        backend computation — dry_run is the only difference. Applying
        activates nothing; it updates the organization records the held
        deviations describe.
      </p>
      <button
        type="button"
        className="stage-a-btn"
        disabled={busy}
        onClick={loadPreview}
      >
        Preview held diff
      </button>
      {error && (
        <p className="stage-a-error" role="alert">
          {error}
        </p>
      )}
      {preview && (
        <>
          <p className="stage-a-dim">
            counts: {JSON.stringify(preview.counts)}
          </p>
          {changes.length === 0 ? (
            <p className="stage-a-dim">
              No changes — records already match the cards.
            </p>
          ) : (
            <>
              <ul>
                {changes.map((c) => (
                  <DiffRow key={c.agent_id} change={c} />
                ))}
              </ul>
              <button
                type="button"
                className="stage-a-btn stage-a-btn-primary"
                disabled={busy}
                onClick={apply}
              >
                Approve and apply materialization
              </button>
            </>
          )}
        </>
      )}
      {result && (
        <pre className="stage-a-pre">{JSON.stringify(result, null, 2)}</pre>
      )}
    </section>
  );
}
